<template>
  <div class="properties-sidebar">
    <div class="properties-sidebar-list">
      <div class="list-content" v-if="componentsClone.length > 0">
        <div class="section section-component">
          <h3>Component</h3>
          <!-- EMR field -->
          <EhrFieldSearchTab :emrJson="emrJson" :selectedKey="aggregatedValues.key" @updateEhrField="updateEhrField"
            scope="HTML" />
          <!-- Part Options -->
          <div class="form-group" v-if="aggregatedValues.key && aggregatedValues.key.startsWith('part')">
            <label for="image-height" class="form-label" style="word-break: break-all;">Part Label ({{
              aggregatedValues.key }})</label>
            <input type="text" class="form-control" placeholder="Has condition XYZ" :value="getPartLabel()"
              @input="updatePartLabel">
          </div>
          <!-- Type -->
          <div class="form-group" v-if="canChangeType()">
            <label class="form-label">Type</label>
            <select type="text" class="form-control" :value="aggregatedValues.type" @change="updateTypeChange">
              <option value="(Multiple Values)" v-if="aggregatedValues.type == '(Multiple Values)'">(Multiple Values)
              </option>
              <option v-for="tp of getComponentTypes()" :key="tp" :value="tp">{{ tp }}</option>
            </select>
          </div>
          <!-- Label -->
          <div class="form-group" v-if="aggregatedValues.type == 'PARAGRAPH'">
            <label class="form-label">Text</label>
            <textarea class="form-control" :value="aggregatedValues.label" @input="updateLabel"></textarea>
          </div>
          <div class="form-group" v-else>
            <label class="form-label">Label</label>
            <input type="text" class="form-control" :value="aggregatedValues.label" @input="updateLabel">
          </div>
        </div>
        <div class="section section-properties">
          <h3>Properties</h3>
          <!-- Checkboxes -->
          <div class="form-group form-group-checkboxes">
            <div class="horizontal-checkbox-line" v-if="canChangeOptional()">
              <CheckBox colorFilled="#EEE" colorLine="#000" :value="!aggregatedValues.optional"
                @updateValue="updateIsRequired" />
              <label>Field is required</label>
            </div>
            <div class="horizontal-checkbox-line">
              <CheckBox colorFilled="#EEE" colorLine="#000" :value="aggregatedValues.condition != null"
                @updateValue="updateIsConditional" />
              <label>Field is conditional</label>
            </div>
            <div class="horizontal-checkbox-line" v-if="aggregatedValues.type == 'PARAGRAPH'">
              <CheckBox colorFilled="#EEE" colorLine="#000" :value="aggregatedValues.whitespace"
                @updateValue="updateAllowWhitespace" />
              <label>Allow whitespace</label>
            </div>
          </div>
          <!-- Image options -->
          <div class="form-group" v-if="aggregatedValues.type == 'IMAGE' && aggregatedValues.width != null">
            <label for="image-width" class="form-label">Image Width</label>
            <input type="number" class="form-control" :value="aggregatedValues.width" @blur="updateImageSize">
          </div>
          <div class="form-group" v-if="aggregatedValues.type == 'IMAGE' && aggregatedValues.width != null">
            <label for="image-width" class="form-label">Update Image</label>
            <input type="file" class="form-control" accept="image/*" @change="updateImageFile">
          </div>
          <!-- Header options -->
          <div class="form-group" v-if="aggregatedValues.type == 'HEADER'">
            <label class="form-label">Header Size</label>
            <select type="text" class="form-control" v-model="aggregatedValues.headerSize" @change="updateHeaderSize">
              <option value="h1">Heading 1</option>
              <option value="h2">Heading 2</option>
              <option value="h3">Heading 3</option>
            </select>
          </div>
          <!-- Signature options -->
          <div class="form-group" v-if="aggregatedValues.type == 'SIGNATURE'">
            <label class="form-label">Signature Type</label>
            <select type="text" class="form-control" :value="aggregatedValues.signatureType"
              @change="updateSignatureType">
              <option value="signature-patient">Patient Signature</option>
              <option value="initials-patient">Patient Initials</option>
              <option value="signature-guardian">Guardian Signature</option>
              <option value="initials-guardian">Guardian Initials</option>
              <option value="signature-witness">Witness Signature</option>
              <option value="initials-witness">Witness Initials</option>
            </select>
          </div>
          <!-- Options List -->
          <OptionsListTab v-if="hasOptionsDropdown()" :options="aggregatedValues.options" :locked="shouldLockOptions()"
            @updateOptions="updateOptions" />
          <!-- Conditional tab -->
          <ConditionalTab v-if="aggregatedValues.condition" :component="componentsClone[0]"
            :allComponents="allComponents" @updateCondition="updateCondition" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EhrFieldSearchTab from '../form-editor-shared/EhrFieldSearchTab.vue'
import OptionsListTab from '../form-editor-shared/OptionsListTab.vue'
import CheckBox from '@/components/CheckBox.vue'
import ConditionalTab from '../form-editor-shared/ConditionalTab.vue'

export default {
  name: 'PropertiesSidebarV2',
  props: {
    emrJson: Object,
    components: Array,
    allComponents: Array
  },
  data() {
    return {
      componentsClone: [],
      aggregatedValues: {}
    }
  },
  watch: {
    components: {
      handler(newVal) {
        if (!newVal || newVal.length === 0) {
          this.componentsClone = [];
          this.aggregatedValues = {};
          return;
        }
        this.componentsClone = newVal.map(c => structuredClone(c));
        this.updateAggregatedValues();
        this.$nextTick(() => {
          const textareas = this.$el.querySelectorAll('textarea');
          textareas.forEach(this.resizeTextArea);
        })
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    updateAggregatedValues() {
      if (this.componentsClone.size === 0) {
        this.aggregatedValues = {};
        return;
      }

      console.log(this.componentsClone);
      const aggregated = {};

      // Key
      for (let component of this.componentsClone) {
        let key = component.key;
        if (key.startsWith('doNotImport')) {
          key = 'doNotImport';
        }

        if (aggregated['key'] == undefined) {
          aggregated['key'] = key;
        } else if (aggregated['key'] != key) {
          aggregated['key'] = '(Multiple Values)';
        }
      }

      // Types
      for (let component of this.componentsClone) {
        if (aggregated['type'] == undefined) {
          aggregated['type'] = component.type;
        } else if (aggregated['type'] != component.type) {
          aggregated['type'] = '(Multiple Values)';
        }
      }

      // Label
      for (let component of this.componentsClone) {
        if (aggregated['label'] == undefined) {
          aggregated['label'] = component.label;
        } else if (aggregated['label'] != component.label) {
          aggregated['label'] = '(Multiple Values)';
        }
      }

      // Optional
      for (let component of this.componentsClone) {
        if (aggregated['optional'] == undefined) {
          aggregated['optional'] = (component.optional || false);
        } else if (aggregated['optional'] != component.optional) {
          aggregated['optional'] = true;
        }
      }

      // Condition
      for (let component of this.componentsClone) {
        if (aggregated['condition'] == undefined) {
          aggregated['condition'] = structuredClone(component.condition);
        } else if (JSON.stringify(aggregated['condition']) != JSON.stringify(component.condition)) {
          aggregated['condition'] = undefined;
        }
      }

      // Whitespace
      for (let component of this.componentsClone) {
        if (aggregated['whitespace'] == undefined) {
          aggregated['whitespace'] = (component.whitespace || false);
        } else if (aggregated['whitespace'] != component.whitespace) {
          aggregated['whitespace'] = false;
        }
      }

      // Options
      for (let component of this.componentsClone) {
        if (aggregated['options'] == undefined) {
          aggregated['options'] = structuredClone(component.options);
        } else if (JSON.stringify(aggregated['options']) != JSON.stringify(component.options)) {
          aggregated['options'] = ['(Multiple Options)'];
        }
      }

      // Header Size
      for (let component of this.componentsClone) {
        if (aggregated['headerSize'] == undefined) {
          aggregated['headerSize'] = component.headerSize;
        } else if (aggregated['headerSize'] != component.headerSize) {
          aggregated['headerSize'] = false;
        }
      }

      // Image Width
      for (let component of this.componentsClone) {
        if (aggregated['width'] == undefined) {
          aggregated['width'] = component.width;
        } else if (aggregated['width'] != component.width) {
          aggregated['width'] = undefined
        }
      }

      // Signature type
      for (let component of this.componentsClone) {
        if (aggregated['signatureType'] == undefined) {
          aggregated['signatureType'] = component.signatureType;
        } else if (aggregated['signatureType'] != component.signatureType) {
          aggregated['signatureType'] = '(Multiple Options)';
        }
      }

      this.aggregatedValues = aggregated;
      console.log(aggregated);
    },
    updateEhrField(field) {
      // Update all selected components
      this.componentsClone.forEach(component => {
        // Update the component key
        if (field.unique) {
          let key = field.key + this.generateRandomNonce();
          if (key.startsWith('part')) {
            const cleanValue = component.label.replace(/[^A-Za-z0-9 ?)(]/g, '');
            key += `_${encodeURIComponent(cleanValue)}`
          }
          component.key = key
        } else {
          component.key = field.key;
        }

        // Update the component name based on field
        if (field.key != 'doNotImport' && !field.key.startsWith('part')) {
          component.label = field.label;
        }

        // Update the component type
        if (field.type != null) {
          component.type = field.type;
        }

        // Required flag
        if (field.optional != null) {
          component.optional = field.optional;
        }

        // Update options
        if (field.options != null) {
          component.options = field.options;
        }
      });

      // Update aggregated values and emit to parent
      this.updateAggregatedValues();
      this.updateComponents();
    },
    updateOptions(options) {
      if (options.length >= 1 && options[0] == '(Multiple Options)') {
        return;
      }

      this.componentsClone.forEach(component => {
        component.options = options;
      });
      this.updateAggregatedValues();
      this.updateComponents();
    },
    updateIsRequired(value) {
      this.componentsClone.forEach(component => {
        component.optional = !value;
      });
      this.updateAggregatedValues();
      this.updateComponents();
    },
    updateIsConditional(value) {
      this.componentsClone.forEach(component => {
        if (value) {
          component.condition = {
            key: '',
            value: '',
          }
        } else {
          component.condition = undefined;
        }
      });
      this.updateAggregatedValues();
      this.updateComponents();
    },
    updateAllowWhitespace(value) {
      this.componentsClone.forEach(component => {
        component.whitespace = value;
      });
      this.updateAggregatedValues();
      this.updateComponents();
    },
    updateCondition(condition) {
      this.componentsClone.forEach(component => {
        component.condition = condition;
      });
      this.updateAggregatedValues();
      this.updateComponents();
    },
    updateSignatureType(event) {
      const value = event.target.value;

      this.componentsClone.forEach(component => {
        component.signatureType = value;
        switch (component.signatureType) {
          case 'signature-patient':
            component.label = 'Patient Signature';
            break;
          case 'signature-guardian':
            component.label = 'Parent or Guardian Signature';
            break;
          case 'signature-witness':
            component.label = 'Witness Signature';
            break;
          case 'initials-patient':
            component.label = 'Patient Initials';
            break;
          case 'initials-guardian':
            component.label = 'Parent or Guardian Initials';
            break;
          case 'initials-witness':
            component.label = 'Witness Initials';
            break;
        }
      });
      this.updateAggregatedValues();
      this.updateComponents();
    },
    generateRandomNonce(length = 10) {
      let id = "";
      for (let i = 0; i < length; i++) {
        id += Math.floor(Math.random() * 10);
      }
      return id;
    },
    shouldLockOptions() {
      const key = this.aggregatedValues.key;
      if (key == null) {
        return false;
      }

      if (key.startsWith('doNotImport')) {
        return false;
      }

      if (key.startsWith('part')) {
        return false;
      }

      return true;
    },
    canChangeType() {
      return this.aggregatedValues.key && (this.aggregatedValues.key.startsWith('doNotImport') || this.aggregatedValues.key.startsWith('part'));
    },
    canChangeOptional() {
      return this.aggregatedValues.type != 'HEADER' && this.aggregatedValues.type != 'PARAGRAPH' && this.aggregatedValues.type != 'IMAGE';
    },
    hasOptionsDropdown() {
      if (!this.aggregatedValues.options) {
        return false;
      }
      const type = this.aggregatedValues.type;
      return type == 'DROPDOWN' || type == 'MULTISELECT';
    },
    resizeTextArea(target) {
      target.style.height = '0px';
      target.style.height = `${target.scrollHeight}px`
    },
    updateLabel(event) {
      const target = event.target;
      if (target.tagName == 'TEXTAREA') {
        this.resizeTextArea(target);
      }

      const newLabel = target.value.trimStart();
      this.componentsClone.forEach(component => {
        component.label = newLabel;
      });
      this.updateAggregatedValues();
      this.updateComponents();
    },
    updateComponents() {
      this.componentsClone.forEach(component => {
        if (component.options == null) {
          if (component.type == 'DROPDOWN' || component.type == 'MULTISELECT') {
            component.options = [];
          }
        }
      });

      this.$emit('updateComponents', Array.from(this.componentsClone));
    },
    getComponentTypes() {
      return ['HEADER', 'PARAGRAPH', 'IMAGE', 'TEXT', 'TEXTAREA', 'NUMBER', 'DATE', 'CHECKBOX', 'DROPDOWN', 'MULTISELECT', 'EMAIL', 'PHONE', 'SSN', 'SIGNATURE', 'FILE', 'SPACER', 'STATE']
    },
    updateImageSize(event) {
      let newWidth = parseInt(event.target.value);
      if (newWidth < 64) newWidth = 64;

      this.componentsClone.forEach(component => {
        if (component.width == null || component.height == null) return;
        const newHeight = Math.round(newWidth * (component.height / component.width));
        component.width = newWidth;
        component.height = newHeight;
      });

      this.updateAggregatedValues();
      this.updateComponents();
    },
    updateImageFile(event) {
      const file = event.target.files[0];
      const fr = new FileReader();
      fr.onload = () => {
        const img = new Image();
        img.onload = () => {
          // Rescale the image
          const width = 256;
          const height = Math.round(256 * img.height / img.width);
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert the canvas to a base64 string
          const base64String = canvas.toDataURL('image/png');
          this.componentsClone.forEach(component => {
            component.data = base64String;
            component.width = width;
            component.height = height;
          });
          this.updateAggregatedValues();
          this.updateComponents();
        }
        img.src = fr.result;
      }
      fr.readAsDataURL(file);
    },
    updatePartLabel(event) {
      const value = event.target.value;
      const cleanValue = value.replace(/[^A-Za-z0-9 ?)(]/g, '');

      this.componentsClone.forEach(component => {
        const key = component.key;
        if (!key) return;

        let keyWithoutSuffix = key;
        if (keyWithoutSuffix.indexOf('_') > 0) {
          keyWithoutSuffix = key.substring(0, keyWithoutSuffix.indexOf('_'));
        }

        component.key = `${keyWithoutSuffix}_${encodeURIComponent(cleanValue)}`;
      });
      this.updateAggregatedValues();
      this.updateComponents();
    },
    getPartLabel() {
      const key = this.aggregatedValues.key;
      if (!key) return;

      if (key.indexOf('_') > 0) {
        return decodeURIComponent(key.substring(key.indexOf('_') + 1));
      }

      return '';
    },
    updateTypeChange(event) {
      const newType = event.target.value;
      this.componentsClone.forEach(component => {
        component.type = newType;
      });
      this.updateAggregatedValues();
      this.updateComponents();
    },
    updateHeaderSize() {
      this.componentsClone.forEach(component => {
        component.headerSize = this.aggregatedValues.headerSize;
      });
      this.updateAggregatedValues();
      this.updateComponents();
    }
  },
  components: { EhrFieldSearchTab, OptionsListTab, ConditionalTab, CheckBox },
  emits: ['updateComponents']
}
</script>

<style scoped>
.properties-sidebar {
  flex-basis: 350px;
  height: 100%;
  padding: 15px;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.properties-sidebar::-webkit-scrollbar {
  display: none;
}

.properties-sidebar-list {
  width: 100%;
  min-height: 100%;
  background: #fff;
  box-shadow: 0px 0px 8px #00000022;
  border-radius: 10px;
}

.section {
  padding: 25px;
  padding-bottom: 50px;
}

.section h3 {
  margin-bottom: 50px;
  text-transform: uppercase;
  border-bottom: 1px solid #000;
  line-height: 40px;
  font-size: 20px;
  letter-spacing: 2px;
}

.form-group-checkboxes {
  margin-top: 0px;
}

.horizontal-checkbox-line {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.horizontal-checkbox-line:not(:first-child) {
  margin-top: 15px;
}

.horizontal-checkbox-line .checkbox {
  flex-shrink: 0;
}

.horizontal-checkbox-line label {
  margin-left: 10px;
  font-size: 16px;
  color: var(--color-secondary);
}

.form-control {
  background: #F5F5F5;
  border-bottom: 1px solid #DDD;
}

textarea.form-control {
  font-size: 16px;
  resize: none;
  overflow: hidden;
}
</style>